import type { StudyEventInput, StudyRecordLinkInput } from '@common/domain/study-telemetry';
import jwtDecode from 'jwt-decode';
import { API_URL } from '../constants';

const STORAGE_KEY_PREFIX = 'learning-workspace-study-events:v1';
const MAX_STORED_EVENTS = 200;
const MAX_BATCH_EVENTS = 20;
const MAX_BATCH_BYTES = 65_536;
const FLUSH_INTERVAL_MS = 5_000;
const RECORD_LINK_ATTEMPTS = 3;
const MAX_FUTURE_SKEW_MS = 5 * 60 * 1_000;
const MAX_IDENTIFIER_LENGTH = 256;
const MAX_SLUG_LENGTH = 128;
const MAX_VIEWPORT_DIMENSION = 100_000;

export function resolveStudyParticipantId(accessToken?: string): string | undefined {
  if (!accessToken) return undefined;
  try {
    const claims = jwtDecode<{ sub?: unknown }>(accessToken);
    if (typeof claims.sub !== 'string') return undefined;
    const participantId = claims.sub.trim();
    return participantId.length > 0 ? participantId : undefined;
  } catch {
    return undefined;
  }
}

type StudyEventBatch = { events: StudyEventInput[]; body: string };

type JsonObject = Record<string, unknown>;

function isObject(value: unknown): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function finiteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function boundedInteger(value: unknown, minimum: number, maximum: number): value is number {
  return finiteNumber(value) && Number.isInteger(value) && value >= minimum && value <= maximum;
}

function validUuid(value: unknown): value is string {
  return typeof value === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function validCanonicalTimestamp(value: unknown): value is string {
  if (typeof value !== 'string'
    || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)) return false;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp)
    && new Date(timestamp).toISOString() === value
    && timestamp <= Date.now() + MAX_FUTURE_SKEW_MS;
}

function validIdentifier(value: unknown): value is string {
  return typeof value === 'string'
    && value.length >= 1
    && value.length <= MAX_IDENTIFIER_LENGTH
    && value.trim() === value;
}

function validSlug(value: unknown): value is string {
  return typeof value === 'string'
    && value.length >= 1
    && value.length <= MAX_SLUG_LENGTH
    && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value);
}

function validPercentage(value: unknown): value is number {
  return finiteNumber(value) && value >= 0 && value <= 100;
}

function percentagesTotalOneHundred(materialPercent: number, teachingPercent: number): boolean {
  return Math.abs(materialPercent + teachingPercent - 100) <= 0.001;
}

function sanitizeViewport(value: unknown): StudyEventInput['viewport'] | undefined {
  if (!isObject(value)
    || !boundedInteger(value.width, 1, MAX_VIEWPORT_DIMENSION)
    || !boundedInteger(value.height, 1, MAX_VIEWPORT_DIMENSION)) return undefined;
  return { width: value.width, height: value.height };
}

function sanitizeLayout(value: unknown): StudyEventInput['layout'] | undefined {
  if (!isObject(value)
    || !validPercentage(value.materialPercent)
    || !validPercentage(value.teachingPercent)
    || !percentagesTotalOneHundred(value.materialPercent, value.teachingPercent)) return undefined;
  return { materialPercent: value.materialPercent, teachingPercent: value.teachingPercent };
}

function sanitizeDurations(value: unknown): {
  materialVisibleMs: number;
  materialActiveMs: number;
  teachingActiveMs: number;
} | undefined {
  if (!isObject(value)
    || !boundedInteger(value.materialVisibleMs, 0, Number.MAX_SAFE_INTEGER)
    || !boundedInteger(value.materialActiveMs, 0, Number.MAX_SAFE_INTEGER)
    || !boundedInteger(value.teachingActiveMs, 0, Number.MAX_SAFE_INTEGER)) return undefined;
  return {
    materialVisibleMs: value.materialVisibleMs,
    materialActiveMs: value.materialActiveMs,
    teachingActiveMs: value.teachingActiveMs,
  };
}

/**
 * Storage is untrusted input. Rebuild the closed event contract rather than
 * persisting or transmitting arbitrary properties from a cast or old schema.
 */
function sanitizeEvent(value: unknown): StudyEventInput | undefined {
  if (!isObject(value)
    || !validUuid(value.eventId)
    || !validUuid(value.studySessionId)
    || !validCanonicalTimestamp(value.occurredAt)
    || !validIdentifier(value.personaId)
    || !validSlug(value.personaSlug)
    || !['textbook', 'chat', 'avatar'].includes(value.modality as string)) return undefined;

  const viewport = sanitizeViewport(value.viewport);
  if (!viewport) return undefined;
  const layout = value.layout === undefined ? undefined : sanitizeLayout(value.layout);
  if (value.layout !== undefined && !layout) return undefined;
  if (value.chapterSlug !== undefined && !validSlug(value.chapterSlug)) return undefined;

  const base = {
    eventId: value.eventId,
    studySessionId: value.studySessionId,
    occurredAt: value.occurredAt,
    personaId: value.personaId,
    personaSlug: value.personaSlug,
    modality: value.modality as StudyEventInput['modality'],
    ...(value.chapterSlug === undefined ? {} : { chapterSlug: value.chapterSlug }),
    viewport,
    ...(layout === undefined ? {} : { layout }),
  };

  switch (value.eventName) {
    case 'study_session_started':
      return { ...base, eventName: value.eventName };
    case 'study_session_ended': {
      const details = sanitizeDurations(value.details);
      return details ? { ...base, eventName: value.eventName, details } : undefined;
    }
    case 'active_surface_changed': {
      const surface = isObject(value.details) ? value.details.surface : undefined;
      return ['material', 'teaching', 'inactive'].includes(surface as string)
        ? { ...base, eventName: value.eventName, details: { surface: surface as 'material' | 'teaching' | 'inactive' } }
        : undefined;
    }
    case 'material_visibility_changed':
    case 'transcript_visibility_changed': {
      const visible = isObject(value.details) ? value.details.visible : undefined;
      return typeof visible === 'boolean'
        ? { ...base, eventName: value.eventName, details: { visible } }
        : undefined;
    }
    case 'material_selected': {
      const chapterSlug = isObject(value.details) ? value.details.chapterSlug : undefined;
      return validSlug(chapterSlug) && value.chapterSlug === chapterSlug
        ? { ...base, eventName: value.eventName, details: { chapterSlug } }
        : undefined;
    }
    case 'material_progress_changed': {
      const durations = sanitizeDurations(value.details);
      const milestone = isObject(value.details) ? value.details.milestone : undefined;
      return durations && value.chapterSlug !== undefined && [25, 50, 75, 100].includes(milestone as number)
        ? {
            ...base,
            eventName: value.eventName,
            details: { ...durations, milestone: milestone as 25 | 50 | 75 | 100 },
          }
        : undefined;
    }
    case 'divider_resized': {
      if (!isObject(value.details)
        || !validPercentage(value.details.materialPercent)
        || !validPercentage(value.details.teachingPercent)
        || !percentagesTotalOneHundred(value.details.materialPercent, value.details.teachingPercent)
        || !['material_expanded', 'teaching_expanded'].includes(value.details.direction as string)
        || layout === undefined
        || layout.materialPercent !== value.details.materialPercent
        || layout.teachingPercent !== value.details.teachingPercent) {
        return undefined;
      }
      return {
        ...base,
        eventName: value.eventName,
        details: {
          materialPercent: value.details.materialPercent,
          teachingPercent: value.details.teachingPercent,
          direction: value.details.direction as 'material_expanded' | 'teaching_expanded',
        },
      };
    }
    default:
      return undefined;
  }
}

function serializedBatch(events: StudyEventInput[]): string {
  return JSON.stringify({ events });
}

function utf8Size(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

function sameServerBatch(left: StudyEventInput, right: StudyEventInput): boolean {
  return left.studySessionId === right.studySessionId
    && left.personaId === right.personaId
    && left.personaSlug === right.personaSlug
    && left.modality === right.modality;
}

function nextBatch(queue: StudyEventInput[]): StudyEventBatch | undefined {
  const first = queue[0];
  if (!first) return undefined;
  const events: StudyEventInput[] = [];
  let body = serializedBatch([first]);
  if (utf8Size(body) > MAX_BATCH_BYTES) return undefined;

  for (const candidate of queue) {
    if (events.length >= MAX_BATCH_EVENTS || !sameServerBatch(first, candidate)) break;
    const candidateEvents = [...events, candidate];
    const candidateBody = serializedBatch(candidateEvents);
    if (utf8Size(candidateBody) > MAX_BATCH_BYTES) break;
    events.push(candidate);
    body = candidateBody;
  }
  return { events, body };
}

export class StudyTelemetryClient {
  private readonly accessToken: string;
  private readonly storage: Storage;
  private readonly storageKey: string;
  private readonly fetchImpl: typeof fetch;
  private queue: StudyEventInput[] = [];
  private inFlight: StudyEventBatch | undefined;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private flushPromise: Promise<void> | undefined;
  private stopPromise: Promise<void> | undefined;
  private stopping = false;
  private stopped = false;
  private unauthorized = false;

  constructor(input: {
    accessToken: string;
    storage: Storage;
    fetchImpl?: typeof fetch;
  }) {
    const participantId = resolveStudyParticipantId(input.accessToken);
    if (!participantId) throw new Error('Study telemetry requires a valid Cognito sub');
    this.accessToken = input.accessToken;
    this.storage = input.storage;
    this.storageKey = `${STORAGE_KEY_PREFIX}:${encodeURIComponent(participantId)}`;
    this.fetchImpl = input.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.queue = this.loadQueue();
    this.scheduleFlush();
  }

  enqueue(event: StudyEventInput): void {
    if (this.stopping || this.stopped) return;
    const safeEvent = sanitizeEvent(event);
    if (!safeEvent || utf8Size(serializedBatch([safeEvent])) > MAX_BATCH_BYTES) return;
    // Never evict an attempted row. At capacity, retain the immutable retry
    // prefix and the newest pending rows, dropping only the oldest pending row.
    const pendingCapacity = MAX_STORED_EVENTS - (this.inFlight?.events.length ?? 0);
    this.queue = pendingCapacity === 0
      ? []
      : [...this.queue, safeEvent].slice(-pendingCapacity);
    this.persistQueue();
    this.scheduleFlush();
  }

  flush(options: { keepalive?: boolean } = {}): Promise<void> {
    if (this.stopped || this.unauthorized) return Promise.resolve();
    if (this.stopping) return this.stopPromise ?? Promise.resolve();
    if (this.flushPromise) return this.flushPromise;
    this.clearTimer();

    const operation = this.drainQueue(options.keepalive === true).finally(() => {
      if (this.flushPromise === operation) this.flushPromise = undefined;
      if (!this.stopping && !this.stopped) this.scheduleFlush();
    });
    this.flushPromise = operation;
    return operation;
  }

  async linkRecord(studySessionId: string, input: StudyRecordLinkInput): Promise<void> {
    const body = JSON.stringify({
      personaId: input.personaId,
      modality: input.modality,
      recordId: input.recordId,
    } satisfies StudyRecordLinkInput);
    let lastError: Error | undefined;
    for (let attempt = 0; attempt < RECORD_LINK_ATTEMPTS; attempt += 1) {
      try {
        const response = await this.fetchImpl(
          `${API_URL}/study-sessions/${encodeURIComponent(studySessionId)}/record-link`,
          {
            method: 'PUT',
            headers: this.headers(),
            body,
          },
        );
        if (response.ok) return;
        lastError = new Error(`Study telemetry record link failed: HTTP ${response.status}`);
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Study telemetry record link failed');
      }
    }
    throw lastError ?? new Error('Study telemetry record link failed');
  }

  stop(): Promise<void> {
    if (this.stopPromise) return this.stopPromise;
    if (this.stopped) return Promise.resolve();
    this.stopping = true;
    this.clearTimer();
    const activeBatch = this.flushPromise === undefined ? undefined : this.inFlight;
    this.stopPromise = (async () => {
      try {
        if (activeBatch) {
          const response = await this.sendBatch(activeBatch, true);
          if (!response.ok) throw this.flushError(response);
          this.acknowledge(activeBatch);
        }
        await this.drainQueue(true);
      } finally {
        this.stopped = true;
        this.stopping = false;
        this.clearTimer();
      }
    })();
    return this.stopPromise;
  }

  private async drainQueue(keepalive: boolean): Promise<void> {
    while (this.inFlight !== undefined || this.queue.length > 0) {
      const batch = this.beginBatch();
      if (!batch) throw new Error('Study telemetry event exceeds 65536 bytes');

      const response = await this.sendBatch(batch, keepalive);
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this.unauthorized = true;
          this.clearTimer();
        }
        throw this.flushError(response);
      }

      this.acknowledge(batch);
      if (this.stopped || (this.stopping && !keepalive)) return;
    }
  }

  private beginBatch(): StudyEventBatch | undefined {
    if (this.inFlight) return this.inFlight;
    const batch = nextBatch(this.queue);
    if (!batch) return undefined;
    this.inFlight = batch;
    this.queue = this.queue.slice(batch.events.length);
    this.persistQueue();
    return batch;
  }

  private sendBatch(batch: StudyEventBatch, keepalive: boolean): Promise<Response> {
    return this.fetchImpl(`${API_URL}/study-events/batch`, {
      method: 'POST',
      headers: this.headers(),
      body: batch.body,
      keepalive,
    });
  }

  private acknowledge(batch: StudyEventBatch): void {
    if (this.inFlight !== batch) return;
    this.inFlight = undefined;
    this.persistQueue();
  }

  private flushError(response: Response): Error {
    return new Error(`Study telemetry flush failed: HTTP ${response.status}`);
  }

  private headers(): Record<string, string> {
    return {
      Authorization: `Bearer ${this.accessToken}`,
      'Content-Type': 'application/json',
    };
  }

  private loadQueue(): StudyEventInput[] {
    try {
      const serialized = this.storage.getItem(this.storageKey);
      if (serialized === null) return [];
      const parsed: unknown = JSON.parse(serialized);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map(sanitizeEvent)
        .filter((item): item is StudyEventInput => item !== undefined)
        .filter((item) => utf8Size(serializedBatch([item])) <= MAX_BATCH_BYTES)
        .slice(-MAX_STORED_EVENTS);
    } catch {
      return [];
    }
  }

  private persistQueue(): void {
    try {
      this.storage.setItem(this.storageKey, JSON.stringify([
        ...(this.inFlight?.events ?? []),
        ...this.queue,
      ]));
    } catch {
      // Storage can be unavailable in privacy modes; the in-memory queue still works.
    }
  }

  private scheduleFlush(): void {
    if (this.timer !== undefined
      || this.flushPromise !== undefined
      || (this.inFlight === undefined && this.queue.length === 0)
      || this.stopping
      || this.stopped
      || this.unauthorized) return;
    this.timer = setTimeout(() => {
      this.timer = undefined;
      void this.flush().catch(() => undefined);
    }, FLUSH_INTERVAL_MS);
  }

  private clearTimer(): void {
    if (this.timer === undefined) return;
    clearTimeout(this.timer);
    this.timer = undefined;
  }
}
